import { createContext, useCallback, useEffect, useReducer, useContext } from 'react'
import { parse } from 'qs'

import { matchTag } from 'utils/languages'

export const ApplicationContext = createContext()
const ApplicationDispatchContext = createContext()

const SET_ORGANIZATION = 'set organization for the context of the application'
const SET_DARK_MODE = 'set dark mode'
const SET_LANGUAGE = 'set language locale'

const { darkMode, organizationId: organizationIdQS } = parse(window.location.search, { ignoreQueryPrefix: true })

const LOCALSTORAGE_KEY = 'hub-application'

function getInitialFeatures() {
  const features = {}
  if (darkMode) {
    features.darkMode = darkMode === '1' ? 1 : 0
  }
  return features
}

/**
 * We used to store the full organization object in local storage, this method ensures that whether an
 * object is stored in LS or just an id, from now on we only use the id
 */
function getOrganizationId(organization) {
  return organization?.id ?? organization
}

export function UNSAFE_getApplicationContext() {
  const { organization, ...applicationContext } = JSON.parse(localStorage.getItem(LOCALSTORAGE_KEY) || '{}')
  return { organization: getOrganizationId(organization), ...applicationContext }
}

function UNSAFE_initApplicationContext() {
  const applicationContext = UNSAFE_getApplicationContext()
  const initialFeatures = getInitialFeatures()
  applicationContext.features = {
    ...applicationContext.features,
    ...initialFeatures,
  }
  return applicationContext
}

export function persistApplicationContext(application) {
  return localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(application))
}

function init() {
  const { organization, ...applicationContext } = UNSAFE_initApplicationContext()
  const organizationContext = organizationIdQS ?? organization
  const sanitizeNullableStringOrganization =
    !['null', 'undefined'].includes(organizationContext) && organizationContext?.match(/[a-f\d]{24}/)
      ? organizationContext
      : undefined

  return { ...applicationContext, organization: sanitizeNullableStringOrganization }
}

function reducer(state, action) {
  const { type, payload } = action

  switch (type) {
    case SET_ORGANIZATION: {
      return { ...state, organization: payload }
    }
    case SET_DARK_MODE: {
      return { ...state, darkMode: payload }
    }
    case SET_LANGUAGE:
      return { ...state, language: payload }
    default:
      throw new Error()
  }
}

function ApplicationProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, init())

  useEffect(() => {
    persistApplicationContext(state)
  }, [state])

  return (
    <ApplicationContext.Provider value={state}>
      <ApplicationDispatchContext.Provider value={dispatch}>{children}</ApplicationDispatchContext.Provider>
    </ApplicationContext.Provider>
  )
}

function useApplicationState() {
  const { language, features } = useContext(ApplicationContext)
  return { language, features }
}

function useApplicationDispatch() {
  const dispatch = useContext(ApplicationDispatchContext)

  const setOrganization = useCallback(
    organization => {
      dispatch({ type: SET_ORGANIZATION, payload: organization })
    },
    [dispatch],
  )

  const setDarkMode = useCallback(
    darkMode => {
      dispatch({ type: SET_DARK_MODE, payload: darkMode })
    },
    [dispatch],
  )

  const setLanguage = useCallback(
    locale => {
      dispatch({ type: SET_LANGUAGE, payload: locale === null ? matchTag() : locale })
    },
    [dispatch],
  )

  return { setOrganization, setDarkMode, setLanguage }
}

function useApplication() {
  const { language, features } = useApplicationState()
  const { setOrganization, setDarkMode, setLanguage } = useApplicationDispatch()

  return {
    language,
    features,
    setOrganization,
    setDarkMode,
    setLanguage,
  }
}

export { ApplicationProvider, useApplication }
